import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  formatBytes,
  formatDate,
  formatDuration,
  formatFrame,
  formatMicroUsd,
  type Generation,
  type ImageAsset,
} from "../api";
import { Lightbox } from "./Lightbox";
import { IconButton } from "./ui";

interface InspectorProps {
  generation: Generation;
  parentSequence?: number;
  onClose: () => void;
  onUseAsInput: (image: ImageAsset) => void;
  onDownload: (image: ImageAsset) => void;
  onDelete: (generation: Generation) => void;
}

/** Everything the record holds about one frame. */
export function Inspector({
  generation,
  parentSequence,
  onClose,
  onUseAsInput,
  onDownload,
  onDelete,
}: InspectorProps) {
  const image: ImageAsset | undefined = generation.outputs[0];
  const [zoomed, setZoomed] = useState(false);

  // On a phone this is a bottom sheet over the stream, so it needs the same two ways out as every
  // other sheet: the close button in the header, which never scrolls away, and Escape.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <aside className="inspector" aria-label={`Frame ${formatFrame(generation.sequence)}`}>
      <div className="inspector-head">
        <h2 className="section-title">
          Frame <span className="mono">{formatFrame(generation.sequence)}</span>
        </h2>
        <IconButton onClick={onClose} label="Close the inspector">
          <X size={16} />
        </IconButton>
      </div>

      <div className="inspector-body">
        {image && (
          <button
            type="button"
            className="print is-static is-zoomable"
            aria-label="View full screen"
            onClick={() => setZoomed(true)}
          >
            {/* Full size here, not the thumbnail: the inspector is where you go to look properly. */}
            <img src={image.content_url} alt={generation.prompt} />
          </button>
        )}

        <p className="prose">{generation.prompt}</p>

        <div className="frame-meta">
          <MetaRow label="Model" value={generation.model_display_name} />
          <MetaRow label="Model id" value={generation.model_id} reading />
          <MetaRow label="Provider" value={generation.provider_id} />
          <MetaRow label="Mode" value={generation.mode === "edit" ? "Edit" : "Generate"} />
          <MetaRow label="Status" value={statusLabel(generation.status)} />
          {parentSequence !== undefined && (
            <MetaRow label="From" value={`Frame ${formatFrame(parentSequence)}`} />
          )}
          {image && <MetaRow label="Size" value={`${image.width}×${image.height}`} reading />}
          {image && <MetaRow label="Weight" value={formatBytes(image.byte_size)} reading />}
          {/* Always an estimate, and never reconciled against an invoice (§18.2). */}
          <MetaRow
            label="Est. cost"
            value={formatMicroUsd(generation.cost_micro_usd)}
            title={generation.cost_basis}
            reading
          />
        </div>

        {Object.keys(generation.parameters).length > 0 && (
          <div className="frame-meta">
            <span className="stamp">Parameters</span>
            {Object.entries(generation.parameters).map(([key, value]) => (
              <MetaRow
                key={key}
                label={labelForKey(key)}
                value={String(value)}
                reading={isReading(value)}
              />
            ))}
          </div>
        )}

        <div className="frame-meta">
          <span className="stamp">Timings</span>
          <MetaRow label="Created" value={formatDate(generation.created_at)} reading />
          {generation.started_at && (
            <MetaRow label="Started" value={formatDate(generation.started_at)} reading />
          )}
          {generation.completed_at && (
            <MetaRow label="Completed" value={formatDate(generation.completed_at)} reading />
          )}
          {generation.duration_ms !== undefined && (
            <MetaRow label="Took" value={formatDuration(generation.duration_ms)} reading />
          )}
        </div>

        {generation.provider_metadata && Object.keys(generation.provider_metadata).length > 0 && (
          <div className="frame-meta">
            <span className="stamp">Provider</span>
            {Object.entries(generation.provider_metadata).map(([key, value]) => (
              <MetaRow key={key} label={labelForKey(key)} value={describeValue(value)} />
            ))}
          </div>
        )}

        {generation.response_text && <p className="lede">{generation.response_text}</p>}
      </div>

      <div className="inspector-actions">
        <button
          type="button"
          className="btn"
          disabled={!image}
          title={image ? undefined : "This frame has no image yet."}
          onClick={() => image && onUseAsInput(image)}
        >
          Use as input
        </button>
        <button
          type="button"
          className="btn"
          disabled={!image}
          title={image ? undefined : "This frame has no image yet."}
          onClick={() => image && onDownload(image)}
        >
          Download
        </button>
        <button type="button" className="btn danger" onClick={() => onDelete(generation)}>
          Delete frame
        </button>
      </div>

      {zoomed && image && (
        <Lightbox image={image} onDownload={onDownload} onClose={() => setZoomed(false)} />
      )}
    </aside>
  );
}

function MetaRow({
  label,
  value,
  reading,
  title,
}: {
  label: string;
  value: string;
  /** Mono is for readings — sizes, durations, money, ids — never for words. */
  reading?: boolean;
  title?: string;
}) {
  return (
    <div className="meta-row">
      <span className="stamp">{label}</span>
      <span className={reading ? "stamp-value" : undefined} title={title}>
        {value}
      </span>
    </div>
  );
}

function statusLabel(status: Generation["status"]): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Keys arrive snake_case on the wire and read as sentences on screen. */
function labelForKey(key: string): string {
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function isReading(value: string | number | boolean): boolean {
  return typeof value === "number" || /^\d/.test(String(value));
}

/** provider_metadata is untyped by contract, so render it without ever printing [object Object]. */
function describeValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
